import { getCriticalityLabel } from "@features/ax/utils";
import { PageHeader } from "@shared/components/PageHeader";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Progress } from "@shared/ui/progress";
import { useState } from "react";
import NetworkCriticalityModal from "../criticalityAssessmentModal";

export default function RiskParameterTab({ data }: { data: any }) {
  const [selectedParameter, setSelectedParameter] = useState<string | null>(
    null
  );
  const [showModal, setShowModal] = useState(false);

  // Ensure criticalityAssessment is always an array
  const riskParameters: any[] = Array.isArray(
    data?.result?.criticalityAssessment
  )
    ? data.result.criticalityAssessment
    : [];

  const getScoreColor = (score: number) => {
    if (score >= 4) return "text-green-600";
    if (score >= 3) return "text-yellow-600";
    return "text-red-600";
  };
  const { label, color } = getCriticalityLabel(data?.result?.criticalityScore);

  if (riskParameters.length === 0) {
    return (
      <Card>
        <div className="flex justify-between items-start p-6 pb-3">
          <PageHeader
            title="Network Risk Assessment Parameters"
            subtitle="Detailed breakdown of risk assessment factors for this network"
          />
          <Button onClick={() => setShowModal(true)}>Assess Criticality</Button>
        </div>
        <CardContent className="mt-3">
          <div className="text-center py-8 text-gray-500">
            No risk parameters data available
          </div>
        </CardContent>
        <NetworkCriticalityModal
          open={showModal}
          onClose={() => setShowModal(false)}
          title="Network Criticality Assessment"
          entityName={data?.result?.name || "Distribution Network"}
        />
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex justify-between items-start p-6 pb-3">
        <PageHeader
          title="Network Risk Assessment Parameters"
          subtitle="Detailed breakdown of risk assessment factors for this network"
        />
        <Button onClick={() => setShowModal(true)}>Update Scores</Button>
      </div>

      <CardContent className="mt-3">
        {riskParameters.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No risk parameters data available
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-center">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                <span className="text-sm text-gray-600 mr-3">
                  Critical Score
                </span>
              </div>
              <div className="text-right">
                <span className={`font-semibold ${color}`}>
                  {data?.result?.criticalityScore ?? "NA"}
                </span>
                <span className={`text-sm ml-2 ${color}`}>{label}</span>
              </div>
            </div>

            {riskParameters.map((param: any, index: number) => (
              <div
                key={index}
                className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() =>
                  setSelectedParameter(
                    selectedParameter === param.name ? null : param.name
                  )
                }
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="font-medium">{param.name}</span>
                      <Badge variant="outline">{param.weight}% Weight</Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div
                        className={`text-lg font-semibold ${getScoreColor(
                          param.score
                        )}`}
                      >
                        {param.score}/5
                      </div>
                      <div className="text-xs text-gray-500">Score</div>
                    </div>
                    <Progress value={param.score * 20} className="w-20 h-2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <NetworkCriticalityModal
        open={showModal}
        onClose={() => setShowModal(false)}
        title="Network Criticality Assessment"
        entityName={data?.result?.name || "Main Distribution Network"}
      />
    </Card>
  );
}
